import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGrades, saveGrade, updateGrade } from '../lib/api/grades';
import { queryKeys } from '../lib/queryClient';
import type { ActivityGradeData } from '../lib/api/types';

export function useGrades(token: string | null, schoolProfileId?: string, activityId?: string, studentId?: string) {
    return useQuery({
        queryKey: activityId 
            ? queryKeys.grades.byActivity(activityId) 
            : studentId 
                ? queryKeys.grades.byStudent(studentId)
                : queryKeys.grades.list(schoolProfileId),
        queryFn: () => getGrades(token!, activityId, studentId),
        enabled: !!token,
        staleTime: 1000 * 10,
    });
}

export function useGradeMutations(token: string, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    const createGrade = useMutation({
        mutationFn: (data: Omit<ActivityGradeData, "id">) => saveGrade(data, token),
        onMutate: async (newGrade) => {
            const keys = [
                queryKeys.grades.list(schoolProfileId),
                queryKeys.grades.byActivity(newGrade.activity_id),
                queryKeys.grades.byStudent(newGrade.student_id)
            ];

            for (const key of keys) {
                await queryClient.cancelQueries({ queryKey: key });
            }

            const snapshots = keys.map(key => ({
                key,
                data: queryClient.getQueryData(key)
            }));

            keys.forEach(key => {
                queryClient.setQueryData(key, (old: any) => [...(old || []), { ...newGrade, id: 'temp-' + Date.now() }]);
            });

            return { snapshots };
        },
        onError: (err, variables, context) => {
            context?.snapshots.forEach(({ key, data }) => {
                queryClient.setQueryData(key, data);
            });
        },
        onSettled: (data, error, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.grades.list(schoolProfileId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.grades.byActivity(variables.activity_id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.grades.byStudent(variables.student_id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        },
    });

    const editGrade = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<ActivityGradeData> }) =>
            updateGrade(id, data, token),
        onMutate: async ({ id, data }) => {
            // We don't know the activity/student ID easily here without looking at existing data
            // but we can try to invalidate the list and any active queries
            await queryClient.cancelQueries({ queryKey: queryKeys.grades.all });
            const previousData = queryClient.getQueryData(queryKeys.grades.list(schoolProfileId));

            return { previousData };
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(queryKeys.grades.list(schoolProfileId), context?.previousData);
        },
        onSettled: (data) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.grades.list(schoolProfileId) });
            if (data?.activity_id) queryClient.invalidateQueries({ queryKey: queryKeys.grades.byActivity(data.activity_id) });
            if (data?.student_id) queryClient.invalidateQueries({ queryKey: queryKeys.grades.byStudent(data.student_id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        },
    });

    return { createGrade, editGrade };
}
